import {
    createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, Outlet, redirect, useRouter,
} from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { CHILD_STATUSES, MESSAGE_SITUATIONS } from '@/contract'
import { PartnerApiError } from '@/lib/api'
import { safeReturnPath } from '@/lib/navigation'
import { applyAccountLocale } from '@/i18n'
import { meQuery } from '@/lib/queries'
import { AppLayout } from '@/components/shell/AppLayout'
import { NetworkErrorScreen, NotFoundScreen, SplashScreen } from '@/components/shell/StatusScreens'

/* Spec PARTNER-001 — arvore de rotas.
   Publicas: /entrar, /pedir-acesso, /esqueci-a-senha (quem ja tem sessao vai para /inicio).
   Com sessao (guarda GET /me): /inicio, /criancas, /criancas/nova, /criancas/$id, /criancas/$id/padrinhos/$sponsorId, /mensagens,
   /configuracoes. */

const LoginPage = lazyRouteComponent(() => import('@/features/auth/pages/LoginPage'), 'LoginPage')
const SignupPage = lazyRouteComponent(() => import('@/features/auth/pages/SignupPage'), 'SignupPage')
const ForgotPasswordPage = lazyRouteComponent(() => import('@/features/auth/pages/ForgotPasswordPage'), 'ForgotPasswordPage')
const HomePage = lazyRouteComponent(() => import('@/features/home/HomePage'), 'HomePage')
const ChildrenListPage = lazyRouteComponent(() => import('@/features/children/ChildrenListPage'), 'ChildrenListPage')
const ChildNewPage = lazyRouteComponent(() => import('@/features/children/ChildDetailPage'), 'ChildNewPage')
const ChildDetailPage = lazyRouteComponent(() => import('@/features/children/ChildDetailPage'), 'ChildDetailPage')
const MessagesPage = lazyRouteComponent(() => import('@/features/messages/MessagesPage'), 'MessagesPage')
const ConversationPage = lazyRouteComponent(() => import('@/features/messages/ConversationPage'), 'ConversationPage')
const SettingsPage = lazyRouteComponent(() => import('@/features/settings/SettingsPage'), 'SettingsPage')

type RouterContext = { queryClient: QueryClient }

/* Sessao valida? 401 PARTNER_SESSION_REQUIRED = nao; outro erro sobe para a tela de falha. */
async function isAuthenticated(queryClient: QueryClient): Promise<boolean> {
    try {
        await queryClient.ensureQueryData(meQuery)
        return true
    } catch (error) {
        if (error instanceof PartnerApiError && error.code === 'PARTNER_SESSION_REQUIRED') return false
        throw error
    }
}

/* Telas publicas: quem ja tem sessao vai para o Inicio (ou para onde ia, ?voltar=). Falha na checagem (servidor fora)
   nao bloqueia a tela. */
const redirectIfAuthenticated = async ({ context, search }: { context: RouterContext; search?: unknown }) => {
    const authenticated = await isAuthenticated(context.queryClient).catch(() => false)
    if (!authenticated) return
    const voltar = safeReturnPath((search as { voltar?: unknown } | undefined)?.voltar)
    throw voltar ? redirect({ href: voltar, replace: true }) : redirect({ to: '/inicio', replace: true })
}

function RootError() {
    const router = useRouter()
    return <NetworkErrorScreen onRetry={() => void router.invalidate()} />
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
    component: Outlet,
    errorComponent: RootError,
    pendingComponent: SplashScreen,
    notFoundComponent: () => <main className="min-h-dvh bg-app-background"><NotFoundScreen /></main>,
})

const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    beforeLoad: async ({ context }) => {
        throw redirect({ to: (await isAuthenticated(context.queryClient)) ? '/inicio' : '/entrar', replace: true })
    },
})

const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/entrar',
    validateSearch: z.object({
        motivo: z.enum(['senha-redefinida']).optional().catch(undefined),
        // PARTNER-002 D-12: para onde voltar depois de entrar (toque no aviso, sessao vencida)
        voltar: z.string().optional().catch(undefined).transform(safeReturnPath),
    }),
    beforeLoad: redirectIfAuthenticated,
    component: LoginPage,
})

const signupRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/pedir-acesso',
    validateSearch: z.object({ etapa: z.enum(['codigo']).optional().catch(undefined) }),
    beforeLoad: redirectIfAuthenticated,
    component: SignupPage,
})

const forgotRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/esqueci-a-senha',
    beforeLoad: redirectIfAuthenticated,
    component: ForgotPasswordPage,
})

/* Guarda de sessao para toda a area autenticada. */
const authRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: '_auth',
    beforeLoad: async ({ context, location }) => {
        if (await isAuthenticated(context.queryClient)) {
            // PARTNER-005 D-03: logado, vale o idioma salvo da pessoa (vence o do aparelho)
            const me = context.queryClient.getQueryData(meQuery.queryKey)
            if (me?.locale) await applyAccountLocale(me.locale)
            return
        }
        // PARTNER-002 D-12: o toque no aviso abre a tela do aviso; depois de entrar, segue para la
        const voltar = safeReturnPath(location.href)
        throw redirect({ to: '/entrar', search: voltar ? { voltar } : {}, replace: true })
    },
    component: AppLayout,
})

const homeRoute = createRoute({ getParentRoute: () => authRoute, path: '/inicio', component: HomePage })

const childrenRoute = createRoute({
    getParentRoute: () => authRoute,
    path: '/criancas',
    validateSearch: z.object({
        busca: z.string().trim().max(100).optional().catch(undefined),
        situacao: z.enum(CHILD_STATUSES).optional().catch(undefined),
    }),
    component: ChildrenListPage,
})
const childNewRoute = createRoute({ getParentRoute: () => authRoute, path: '/criancas/nova', component: ChildNewPage })
const childDetailRoute = createRoute({ getParentRoute: () => authRoute, path: '/criancas/$id', component: ChildDetailPage })
// Spec PWA-012: a OSP escreve primeiro ou retoma a conversa com um padrinho da crianca
const conversationRoute = createRoute({ getParentRoute: () => authRoute, path: '/criancas/$id/padrinhos/$sponsorId', component: ConversationPage })

// Spec PWA-009: mensagens dos padrinhos para as criancas da OSP
const messagesRoute = createRoute({
    getParentRoute: () => authRoute,
    path: '/mensagens',
    validateSearch: z.object({ situacao: z.enum(MESSAGE_SITUATIONS).optional().catch(undefined) }),
    component: MessagesPage,
})

// Spec PARTNER-004: Configuracoes (aberta pelo menu da conta; sem item na barra de baixo, C#1)
const settingsRoute = createRoute({ getParentRoute: () => authRoute, path: '/configuracoes', component: SettingsPage })

const routeTree = rootRoute.addChildren([
    indexRoute,
    loginRoute,
    signupRoute,
    forgotRoute,
    authRoute.addChildren([
        homeRoute,
        childrenRoute,
        childNewRoute,
        childDetailRoute,
        conversationRoute,
        messagesRoute,
        settingsRoute,
    ]),
])

export function createAppRouter(queryClient: QueryClient) {
    return createRouter({
        routeTree,
        context: { queryClient },
        defaultPreload: 'intent',
        defaultPendingComponent: SplashScreen,
        scrollRestoration: true,
    })
}

declare module '@tanstack/react-router' {
    interface Register {
        router: ReturnType<typeof createAppRouter>
    }
}
